import {useState, useEffect} from 'react';
import { DialogProps, Stack, Grid2 as Grid, Button, Checkbox, Typography } from '@mui/material';
import {CommonDialogBox} from "components";
import TerrainIcon from '@mui/icons-material/Terrain';
import AddViewPanel from "panels/AddViewPanel";
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import { tss } from "tss-react/mui";
import { ReactComponent as Mesh } from 'assets/images/ic_geo-mesh.svg'
import { ReactComponent as PointCloud } from 'assets/images/ic_point-cloud.svg';
import { ReactComponent as Ortho } from 'assets/images/ortho.svg';
import { ReactComponent as Heightmap } from 'assets/images/rawhm.svg';
import { ReactComponent as View } from 'assets/images/view.svg';
import { ReactComponent as IndexedMesh } from 'assets/images/idxmesh.svg';
import { ReactComponent as BlockModel } from 'assets/images/rawbm.svg';
import { ReactComponent as DrillHoleDatabase } from 'assets/images/idxdh.svg';
import { ReactComponent as IndexedOrtho } from 'assets/images/idxortho.svg';
import CustomScroll from "components/CustomScroll";
import FlightIcon from '@mui/icons-material/Flight';
import { 
    closeObjectDialog, 
    openDialog, 
    openVoxelTerrainDialog, 
    openIndexedPointDialog,
    openOrthoVoxelTerrainDialog,
    openVoxelizedMeshDialog,
    openIndexedMeshDialog,
    openVoxelBlockModelDialog,
    openVoxelPointCloudDialog,
    openDrillHoleDatabaseDialog,
    openGeoChemDialog,
    openOutputUnityDialog,
    openIndexedImageryDialog
} from 'store/main/viewObjects';
import { useDispatch, useSelector} from 'react-redux';
import { RootState } from 'store';
import { getCachedData } from "api/Cache";
import { Scrollbars } from "react-custom-scrollbars";
import { trackEvent } from 'components/Tracking';

export const useStyles = tss.create(({ theme }) => ({
    root: {
        width: "100%",
        height: "calc(100% - 133px)",
        minHeight: 200,
        position: "relative",
    },
    objectItem: {
        padding: '10px 5px', 
        borderRadius: '8px', 
        margin: '5px 0 5px 0', 
        flexDirection: 'column', 
        display:'flex', 
        textAlign: 'center', 
        width:'150px', 
        boxShadow: theme.shadows[4],
        "&:hover": {
            backgroundColor: '#cccccc30',
            "& a": {
                color: theme.palette.primary.main
            },
            "& svg": {
                color: theme.palette.primary.main,
                fill: theme.palette.primary.main,
                "& path": {
                    stroke: theme.palette.primary.main,
                }
            },
        },
        '& a': {
            textDecoration : 'none',
            color: theme.palette.text.secondary,
            whiteSpace: 'nowrap'
        },
        "& svg": {
            color: theme.palette.text.primary,
            fill: theme.palette.text.primary,
            "& path": {
                stroke: theme.palette.text.primary,
            }
        },
    },
    mainTitle: {
        fontWeight:500, 
        marginBottom: '10px', 
        fontSize: '18px',
        padding: "8px 12px",
        backgroundColor: "rgba(32,32,32,.05)",
        width: "100%",
        "& span": {
            color: theme.palette.primary.main,
            " &.subtitle": {
                color: theme.palette.text.secondary,
                fontSize: '14px',
            }
        },
        display:'flex',
        justifyContent: 'space-between'
    },
    helpText: {
        fontSize: '15px',
        LineWeight: '23px',
        margin: '0px',
        padding: '0px 0px 10px 0px',
        "& ul": {
            columns: 2,
            columnGap: '20px'
        }
    }
}))

const ObjectsListHelp = (props:any) => {
    const { classes, cx } = useStyles();
    const { onClick, onCheckBoxChange } = props;

    return (
        <>
            <CustomScroll
                autoHeight
                autoHeightMax={600}
                style={{ overflowX: "hidden", width: "100%" }}
            >
                <div style={{paddingRight: '10px'}}>
                    <div style={{ display:'flex', flexDirection:'column', justifyContent: 'space-between', width: "100%", paddingBottom: '0px'}}>
                        <div className={cx(classes.mainTitle)}><span>Step 1:</span> Add Spatial Entities</div>
                        <p className={classes.helpText }>
                            VoxelSpace supports a wide range of input formats:
                            <ul>
                                <li>Point Clouds</li>
                                <li>Drill Holes</li>
                                <li>Geo Chem</li>
                                <li>Meshes (OBJ/FBX)</li>
                                <li>Block Models</li>
                                <li>Ortho-Imagery</li>
                            </ul>
                        </p>
                    </div> 
                    <div style={{ display:'flex', flexDirection:'column', justifyContent: 'space-between', width: "100%", paddingBottom: '20px'}}>
                        <div className={cx(classes.mainTitle)}><span>Step 2:</span> Process Spatial Entities</div>
                        <p className={classes.helpText }>
                            Once your raw data is in the system, VoxelSpace lets you convert it into optimized formats for analysis and visualization. These include:
                            <ul >
                                <li>Indexed Mesh</li>
                                <li>Indexed Point Cloud</li>
                                <li>Voxel Terrain</li>
                                <li>Voxel Block Model</li>
                                <li>Voxelized Mesh</li>
                                <li>Ortho Voxel Terrain</li>
                                <li>Drill Hole Database</li>
                                <li>Geo Chem</li>
                            </ul>
                        </p>
                    </div> 
                    <div style={{ display:'flex', flexDirection:'column', justifyContent: 'space-between', width: "100%", paddingBottom: '20px'}}>
                        <div className={cx(classes.mainTitle)}><span>Step 3:</span> Create a View</div>
                        <p className={classes.helpText }>
                            Views are where VoxelSpace truly shines. With a View, you can combine multiple datasets into a 3D scene, configure visual styles, explore cross-sections, and share insights with your team.
                        </p>
                    </div> 
                </div>
            </CustomScroll>
            <div style={{display:'flex', justifyContent: 'space-between', width: "100%"}}>
                 <label style={{display: 'flex', gap: '8px', cursor: 'pointer', alignItems: 'center'}}>
                    <Checkbox
                        color='primary'
                        onChange={onCheckBoxChange}
                        style={{paddingLeft: '0px'}}
                    />
                    <Typography
                        fontSize={'14px'}
                        variant={'body1'}
                        color={'text.secondary'}
                    >
                        Don't show this again
                    </Typography>
                </label>
                <Button 
                    onClick={(ev: any) => {
                        ev.stopPropagation();
                        onClick && onClick();
                    }}
                    variant="contained" 
                    size="small"
                >
                    Continue
                </Button>
            </div>
        </>
    )

}

const ObjectsListPanel = (props:any) => {
   
    const data = getCachedData();
    const [listObjects, setListObjects] = useState<any>([])
    const [newView, setNewView] = useState(false)
    const dispatch = useDispatch();
    const viewObject = useSelector((state: RootState) => state.main.viewObject);
    const {project, objState:open, automaticObject, automaticProcess } = viewObject;    
    const { classes, cx } = useStyles();
    const displayProcessHelp = localStorage.getItem('displayProcessHelp');
    const [showNextTime, setShowNextTime] = useState(parseInt(displayProcessHelp || '0') < 1)
    const [displayHelp, setDisplayHelp] = useState(showNextTime)
      
    const closeComposeDialog: DialogProps["onClose"] = (event, reason) => {
        dispatch(closeObjectDialog({}))
        setDisplayHelp(showNextTime);
    };

    const openView = (e:any) => {
        e.preventDefault()
        setNewView(true)
        dispatch(closeObjectDialog({}))
    }

    const closeView = () => {
        setNewView(false);
    };

    const showObjectPanel = (e:any, objName:string) => {
        e?.preventDefault()
        dispatch(openDialog({objName : objName}))
        dispatch(closeObjectDialog({}))
    }

    const showOutputPanel = (e:any, objName:string) => {
        e.preventDefault()
        dispatch(closeObjectDialog({}))
        switch (objName) {
            case 'Unity':
                dispatch(openOutputUnityDialog({objName : objName}))
              break;
            default:
                console.log('Provide with a valid object name');
        }
    }

    const onCheckBoxChange =(e: any) => {
        const { checked } = e.target;
        setShowNextTime(!checked);
    }

    const showProcessPanel = (e:any, objName:string, objType: string, listObjects : any) => {
        e?.preventDefault()

        dispatch(closeObjectDialog({}))
        switch (objName) {
            case 'Voxel Terrain':
                dispatch(openVoxelTerrainDialog({objName : objName, objType : objType, objSources : [...listObjects]}))
              break;
            case 'Ortho Voxel Terrain':
                dispatch(openOrthoVoxelTerrainDialog({objName : objName, objType : objType, objSources : [...listObjects]}))
              break;
            case 'Indexed Point':
                dispatch(openIndexedPointDialog({objName : objName, objType : objType, objSources : [...listObjects]}))
                break;
            case 'Voxelized Mesh':
                dispatch(openVoxelizedMeshDialog({objName : objName, objType : objType, objSources : [...listObjects]}))
                break;
            case 'Indexed Mesh':
                dispatch(openIndexedMeshDialog({objName : objName, objType : objType, objSources : [...listObjects]}))
                break;
            case 'Indexed Imagery':
                dispatch(openIndexedImageryDialog({objName : objName, objType : objType, objSources : [...listObjects]}))
                break;
            case 'Voxel Block Model':
                dispatch(openVoxelBlockModelDialog({objName : objName, objType : objType, objSources : [...listObjects]}))
                break;
            case 'Voxel Point Cloud':
                dispatch(openVoxelPointCloudDialog({objName : objName, objType : objType, objSources : [...listObjects]}))
                break;
            case 'Drill Hole Database':
                dispatch(openDrillHoleDatabaseDialog({objName : objName, objType : objType, objSources : [...listObjects]}))
                break;
            case 'Geo Chem':
                dispatch(openGeoChemDialog({objName : objName, objType : objType, objSources : [...listObjects]}))
                break;
            default:
              console.log('Provide with a valid object name');
        }
    }

    useEffect(() => {
       
        var allEntities = data["entities"][project];
        let allObjts : {type : string, name : string, id : string, project : string, file_folder : string}[] = [];
        if(typeof allEntities !== "undefined") {
            
            const keys = Object.keys(allEntities);
            keys.forEach((key, index)  => {
                if(allEntities[key]['type'] === 'FILE' || allEntities[key]['type'] === 'FOLDER'){
                    //console.log("All entities : " + JSON.stringify(allEntities[key]))
                    allObjts.push({type: allEntities[key]['file_type'], name: allEntities[key]['name'], id : allEntities[key]['ID'], project : allEntities[key]['project'], file_folder : allEntities[key]['file_folder']})
                }
            })
            setListObjects(allObjts);
        }
    }, [data, project])

    useEffect(() => {
        if (!automaticObject) return;
        
        dispatch(closeObjectDialog({}))
        showObjectPanel(null, automaticObject);
    }, [automaticObject])
    

    useEffect(() => {
        if (!automaticProcess) return;
        
        dispatch(closeObjectDialog({}));
        setTimeout(() => {
            switch(automaticProcess) {
                case 'Voxel Terrain':
                case 'Indexed Point':
                    showProcessPanel(null, automaticProcess, 'RAWPC', listObjects);
                    break;
                case 'Ortho Voxel Terrain':
                case 'Voxelized Mesh':
                case 'Indexed Mesh':
                    showProcessPanel(null, automaticProcess, 'RAWMESH', listObjects);
                    break;
                case 'Indexed Imagery':
                    showProcessPanel(null, automaticProcess, 'ORTHO', listObjects);
                    break;
                case 'Voxel Block Model':
                    showProcessPanel(null, automaticProcess, 'RAWBM', listObjects);
                    break;
                case 'Drill Hole Database':
                    showProcessPanel(null, automaticProcess, 'RAWDH', listObjects);
                    break;
                case 'Geo Chem':
                    showProcessPanel(null, automaticProcess, 'RAWGEOCHEM', listObjects);
                    break;
            }
        }, 1000)
    }, [listObjects, automaticProcess])
    

    return (
        <>
        <CommonDialogBox
            classes={{
                paper: 'm-24 rounded-8'
            }}
            open={open}
            handleClose={closeComposeDialog}
            fullWidth
            maxWidth="md"
            subtitle1="Add New Object"
            titleh5=''
            maxHeight="85%"
            helpLinkCode="project-objects"
        >
            { displayHelp ? (
                <ObjectsListHelp 
                    onClick={ () => {
                        setDisplayHelp(false);
                        !showNextTime && localStorage.setItem('displayProcessHelp', '1');
                    }}
                    onCheckBoxChange={onCheckBoxChange}
                />
            ) : (
                <div style={{ display:'flex', flexDirection:'column', justifyContent: 'space-evenly', alignItems: 'flex-start', gap: '35px'}}>
                    <CustomScroll
                        autoHeight
                        autoHeightMax={500}
                        style={{ overflowX: "hidden", width: "100%" }}
                    >
                    <div style={{ display:'flex', flexDirection:'column', justifyContent: 'space-between', width: "100%"}}>
                        <div className={cx(classes.mainTitle)}><span>Raw Data</span><span className="subtitle">( Step 1 )</span></div>
                        <Grid container spacing={2}>
                            <Grid  size={{sm:6, md:4, lg:3, xl:3}}>    
                                <div className={cx(classes.objectItem)}>
                                    <a href="/#" 
                                        onClick={(e) => {
                                            e.preventDefault()
                                            trackEvent("Raw Data", "Add Object", "Point Cloud");
                                            showObjectPanel(e, "Point Cloud");
                                        }}
                                    >
                                        <div><PointCloud  style={{height:"30px", width:"30px"}}/></div>
                                        <div>Point Cloud</div>
                                    </a>
                                </div>
                            </Grid>
                            <Grid  size={{sm:6, md:4, lg:3, xl:3}}> 
                                <div className={cx(classes.objectItem)}>
                                <a href="/#" 
                                    onClick={(e) => {
                                        e.preventDefault()
                                        trackEvent("Raw Data", "Add Object", "Drill Holes");
                                        showObjectPanel(e, "Drill Holes");
                                    }}
                                >
                                    <div><PointCloud  style={{height:"30px", width:"30px"}}/></div>
                                    <div>Drill Holes</div>
                                </a>
                            </div>
                            </Grid>
                            <Grid  size={{sm:6, md:4, lg:3, xl:3}}> 
                                <div className={cx(classes.objectItem)}>
                                <a href="/#" 
                                    onClick={(e) => {
                                        e.preventDefault()
                                        trackEvent("Raw Data", "Add Object", "Geo Chem");
                                        showObjectPanel(e, "Geo Chem");
                                    }}
                                >
                                    <div><PointCloud  style={{height:"30px", width:"30px"}}/></div>
                                    <div>Geo Chem</div>
                                </a>
                            </div>
                            </Grid>
                            <Grid  size={{sm:6, md:4, lg:3, xl:3}}> 
                                <div className={cx(classes.objectItem)}>
                                <a href="/#" 
                                    onClick={(e) => {
                                        e.preventDefault()
                                        trackEvent("Raw Data", "Add Object", "Mesh");
                                        showObjectPanel(e, "Mesh");
                                    }}
                                >
                                    <div><Mesh  style={{height:"30px", width:"30px"}} /></div>
                                    <div>Mesh</div>
                                </a>
                            </div>
                            </Grid>
                            <Grid  size={{sm:6, md:4, lg:3, xl:3}}> 
                                <div className={cx(classes.objectItem)}>
                                <a href="/#" 
                                    onClick={(e) => {
                                        e.preventDefault()
                                        trackEvent("Raw Data", "Add Object", "Block Model");
                                        showObjectPanel(e, "Block Model");
                                    }}
                                >
                                    <div><BlockModel  style={{height:"30px", width:"30px"}} /></div>
                                    <div>Block Model</div>
                                </a>
                            </div>
                            </Grid>
                            <Grid  size={{sm:6, md:4, lg:3, xl:3}}> 
                                <div className={cx(classes.objectItem)}>
                                <a href="/#" 
                                     onClick={(e) => {
                                        e.preventDefault()
                                        trackEvent("Raw Data", "Add Object", "Ortho-Imagery");
                                        showObjectPanel(e, "Ortho-Imagery");
                                    }}
                                >
                                    <div><Ortho style={{height:"30px", width:"30px"}} /></div>
                                    <div>Ortho-Imagery</div>
                                </a>
                            </div>
                            </Grid>
                            <Grid  size={{sm:6, md:4, lg:3, xl:3}}> 
                                <div className={cx(classes.objectItem)}>
                                <a href="/#" 
                                    onClick={(e) => {
                                        e.preventDefault()
                                        trackEvent("Raw Data", "Add Object", "Heightmap");
                                        showObjectPanel(e, "Heightmap");
                                    }}
                                >
                                    <div><Heightmap style={{height:"30px", width:"30px"}} /></div>
                                    <div>Heightmap</div>
                                </a>
                            </div>
                            </Grid>
                        </Grid>
                    </div> 
                    
                    <div style={{ display:'flex', flexDirection:'column', justifyContent: 'space-between'}}>
                        <div className={cx(classes.mainTitle)}><span>Processed</span><span className="subtitle">( Step 2 )</span></div>
                        <Grid container spacing={2}>
                            <Grid  size={{sm:6, md:4, lg:3, xl:3}}>    
                                <div className={cx(classes.objectItem)}>
                                    <a href="/#" 
                                        onClick={(e) => {
                                            e.preventDefault()
                                            trackEvent("Process", "Voxel Terrain", "Open");
                                            showProcessPanel(e, 'Voxel Terrain', 'RAWPC', listObjects);
                                        }}
                                        >
                                        <div><Ortho style={{height:"30px", width:"30px"}} /></div>
                                        <div>Voxel Terrain</div>
                                    </a>
                                </div>
                            </Grid>
                            <Grid  size={{sm:6, md:4, lg:3, xl:3}}>    
                                <div className={cx(classes.objectItem)}>
                                    <a href="/#" 
                                        onClick={(e) => {
                                            e.preventDefault()
                                            trackEvent("Process", "Indexed Point", "Open");
                                            showProcessPanel(e, 'Indexed Point', 'RAWPC', listObjects);
                                        }}
                                    >
                                        <div><PointCloud  style={{height:"30px", width:"30px"}}/></div>
                                        <div>Indexed Points</div>
                                    </a>
                                </div>
                            </Grid>
                            <Grid  size={{sm:6, md:4, lg:3, xl:3}}>    
                                <div className={cx(classes.objectItem)}>
                                    <a href="/#" 
                                        onClick={(e) => {
                                            e.preventDefault()
                                            trackEvent("Process", "Drill Hole Database", "Open");
                                            showProcessPanel(e, 'Drill Hole Database', 'RAWDH', listObjects);
                                        }}
                                   >
                                        <div><DrillHoleDatabase  style={{height:"30px", width:"30px"}} /></div>
                                        <div>Drill Hole Database</div>
                                    </a>
                                </div>
                            </Grid>
                            <Grid  size={{sm:6, md:4, lg:3, xl:3}}>    
                                <div className={cx(classes.objectItem)}>
                                    <a href="/#" 
                                        onClick={(e) => {
                                            e.preventDefault()
                                            trackEvent("Process", "Geo Chem", "Open");
                                            showProcessPanel(e, 'Geo Chem', 'RAWGEOCHEM', listObjects);
                                        }}
                                   >
                                        <div><DrillHoleDatabase  style={{height:"30px", width:"30px"}} /></div>
                                        <div>Geo Chem</div>
                                    </a>
                                </div>
                            </Grid>
                            <Grid  size={{sm:6, md:4, lg:3, xl:3}}>    
                                <div className={cx(classes.objectItem)}>
                                    <a href="/#" 
                                        onClick={(e) => {
                                            e.preventDefault()
                                            trackEvent("Process", "Ortho Voxel Terrain", "Open");
                                            showProcessPanel(e, 'Ortho Voxel Terrain', 'RAWMESH', listObjects);
                                        }}
                                    >
                                        <div><Ortho  style={{height:"30px", width:"30px"}} /></div>
                                        <div>Ortho Voxel Terrain</div>
                                    </a>
                                </div>
                            </Grid>
                            <Grid  size={{sm:6, md:4, lg:3, xl:3}}>    
                                <div className={cx(classes.objectItem)}>
                                    <a href="/#" 
                                        onClick={(e) => {
                                            e.preventDefault()
                                            trackEvent("Process", "Voxelized Mesh", "Open");
                                            showProcessPanel(e, 'Voxelized Mesh', 'RAWMESH', listObjects);
                                        }}
                                    >
                                        <div><TerrainIcon style={{fontSize:"30px"}} /></div>
                                        <div>Voxelized Mesh</div>
                                    </a>
                                </div>
                            </Grid>
                            <Grid  size={{sm:6, md:4, lg:3, xl:3}}>    
                                <div className={cx(classes.objectItem)}>
                                    <a href="/#" 
                                        onClick={(e) => {
                                            e.preventDefault()
                                            trackEvent("Process", "Indexed Mesh", "Open");
                                            showProcessPanel(e, 'Indexed Mesh', 'RAWMESH', listObjects);
                                        }}
                                    >
                                        <div><IndexedMesh  style={{height:"30px", width:"30px"}} /></div>
                                        <div>Indexed Mesh</div>
                                    </a>
                                </div>
                            </Grid>
                            <Grid  size={{sm:6, md:4, lg:3, xl:3}}>    
                                <div className={cx(classes.objectItem)}>
                                    <a href="/#" 
                                        onClick={(e) => {
                                            e.preventDefault()
                                            trackEvent("Process", "Indexed Imagery", "Open");
                                            showProcessPanel(e, 'Indexed Imagery', 'ORTHO', listObjects);
                                        }}
                                    >
                                        <div><FlightIcon  style={{height:"30px", width:"30px"}} /></div>
                                        <div>Indexed Imagery</div>
                                    </a>
                                </div>
                            </Grid>
                            <Grid  size={{sm:6, md:4, lg:3, xl:3}}>    
                                <div className={cx(classes.objectItem)}>
                                    <a href="/#" 
                                        onClick={(e) => {
                                            e.preventDefault()
                                            trackEvent("Process", "Voxel Block Model", "Open");
                                            showProcessPanel(e, 'Voxel Block Model', 'RAWBM', listObjects);
                                        }}
                                   >
                                        <div><BlockModel  style={{height:"30px", width:"30px"}} /></div>
                                        <div>Voxel Block Model</div>
                                    </a>
                                </div>
                            </Grid>
                            
                            {/* <Grid  size={{sm:6, md:4, lg:3, xl:3}}>    
                                <div className={cx(classes.objectItem)}>
                                    <a href="/#" onClick={(e) => showProcessPanel(e, 'Voxel Point Cloud', 'VOXPC', listObjects)}>
                                        <div><PointCloud  style={{height:"30px", width:"30px"}} /></div>
                                        <div>Voxelized Points</div>
                                    </a>
                                </div>
                            </Grid> */}
                        </Grid>
                    </div> 
                    <div style={{ display:'flex', flexDirection:'column', justifyContent: 'space-between', width: "100%"}}>
                        <div className={cx(classes.mainTitle)}><span>Output</span><span className="subtitle">( Step 3 )</span></div>
                        <Stack 
                            gap={5}
                            direction="row"
                            sx={{
                                //justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <div className={cx(classes.objectItem)}>
                                <a href="/#" 
                                    onClick={(e) => {
                                            e.preventDefault()
                                            trackEvent("Output", "Unity", "Open");
                                            showOutputPanel(e, 'Unity');
                                        }}
                                    >
                                    <div><ViewInArIcon style={{height:"30px", width:"30px"}} /></div>
                                    <div>Unity Project</div>
                                </a>
                            </div>

                            <div className={cx(classes.objectItem)}>
                                <a href="/#" 
                                    onClick={(e) => {
                                            e.preventDefault()
                                            trackEvent("Output", "View", "Open");
                                            openView(e);
                                        }}
                                    >
                                    <div><View style={{height:"30px", width:"30px"}}/></div>
                                    <div>Create View</div>
                                </a>
                            </div>
                            
                        
                        </Stack>
                    </div>
                </CustomScroll>
                </div>
            )}
        </CommonDialogBox>
        {newView ? <AddViewPanel handleClose={closeView} /> : ""}     
        </>
    )
}

export default ObjectsListPanel